import { isCoreCaptureKind } from '@monrdv/shared';
import { reference, type LocalStop } from './localdb.js';
import { text, type Locale } from './format.js';

/**
 * What is left to do at this stop.
 *
 * **Progress is work remaining, not a percentage.** A bar that reads 60% invites the question
 * "60% of what", and on a job whose stages are a pipeline the board already says where it is.
 * What a technician and a coordinator both need is the checklist — *captures 3 of 5 · readings
 * 2 of 4 · time running · parts 2 · report not signed* — where each item is either done or it
 * is not, and every one of them is a thing somebody can act on.
 *
 * The checklist is not invented here. It comes from **the service's own definition of done**:
 * an ordered list of photographs, readings, confirmations and signatures declared once per
 * service, in the business's words. A duct cleaning declares *before, after, airflow before,
 * airflow after*; an air-conditioning installation declares a site survey, both units, a
 * nameplate, commissioning readings and an acceptance signature. A plumber declares neither.
 */

export type Requirement = {
  id: string;
  serviceId: string;
  type: 'photo' | 'reading' | 'confirmation' | 'signature';
  key: string;
  label: unknown;
  required: boolean;
  unit: string | null;
  min: number | null;
  max: number | null;
  phase: 'before' | 'after' | 'both' | 'single';
  formula: string | null;
};

export type CaptureKind = { id: string; key: string; name: unknown };

export type RequirementStatus = Requirement & {
  /** Resolved label, in the language asked for. */
  title: string;
  done: boolean;
  /** For a `both` reading: which halves are in. */
  before?: boolean;
  after?: boolean;
  /** Set when a value is outside the range the service declared. */
  outOfSpec?: boolean;
};

/** What a stop still owes, in the order the service declared it. */
export async function requirementsFor(stop: LocalStop, locale: Locale = 'fr'): Promise<RequirementStatus[]> {
  const all = await reference<Requirement[]>('captureRequirements', []);
  const serviceIds = new Set(stop.services.map((service) => service.id));
  const mine = all.filter((requirement) => serviceIds.has(requirement.serviceId));

  const captureKinds = new Set(stop.captures.map((capture) => capture.kind));
  const readingsByKey = new Map<string, LocalStop['readings']>();
  for (const reading of stop.readings) {
    const list = readingsByKey.get(reading.key) ?? [];
    list.push(reading);
    readingsByKey.set(reading.key, list);
  }

  return mine.map((requirement) => {
    const title = text(requirement.label, locale);

    if (requirement.type === 'reading') {
      const taken = readingsByKey.get(requirement.key) ?? [];
      const before = taken.some((reading) => reading.phase === 'before');
      const after = taken.some((reading) => reading.phase === 'after');
      const single = taken.some((reading) => reading.phase === 'single');
      const done =
        requirement.phase === 'both' ? before && after
        : requirement.phase === 'before' ? before
        : requirement.phase === 'after' ? after
        : single || before || after;
      return { ...requirement, title, done, before, after, outOfSpec: taken.some((r) => r.outOfSpec) };
    }

    // A photograph, a signature or a confirmation is done when something of that kind exists
    // on the stop. The kind is the join, which is exactly why an untyped pile of photographs
    // could never answer this question.
    return { ...requirement, title, done: captureKinds.has(requirement.key) };
  });
}

export type Checklist = {
  captures: { done: number; total: number };
  readings: { done: number; total: number };
  signatures: { done: number; total: number };
  parts: number;
  /** Any reading outside the range its service declared. Shows on the stop and in the report. */
  outOfSpec: boolean;
  /** Everything required that is not yet done. What *done* is blocked on. */
  outstanding: RequirementStatus[];
};

export function summarise(requirements: RequirementStatus[], stop: LocalStop): Checklist {
  const count = (type: RequirementStatus['type']) => {
    const of = requirements.filter((requirement) => requirement.type === type);
    return { done: of.filter((requirement) => requirement.done).length, total: of.length };
  };

  return {
    captures: count('photo'),
    readings: count('reading'),
    signatures: count('signature'),
    parts: stop.parts.length,
    outOfSpec: stop.readings.some((reading) => reading.outOfSpec),
    outstanding: requirements.filter((requirement) => requirement.required && !requirement.done),
  };
}

/**
 * The label for a capture kind, core or declared.
 *
 * The core kinds are the app's and are translated here; anything else is a row the business
 * owns, in its own French, and is looked up rather than guessed. A kind with no row is shown
 * as its key rather than hidden — a photograph nobody can name is still a photograph, and
 * silently dropping it from the gallery is worse than an ugly label.
 */
const CORE_LABELS: Record<string, { fr: string; en: string }> = {
  before: { fr: 'Avant', en: 'Before' },
  after: { fr: 'Après', en: 'After' },
  defect: { fr: 'Défaut', en: 'Defect' },
  hazard: { fr: 'Danger', en: 'Hazard' },
  nameplate: { fr: 'Plaque signalétique', en: 'Nameplate' },
  access: { fr: 'Accès', en: 'Access' },
  attempted_visit: { fr: 'Visite manquée', en: 'Attempted visit' },
  document: { fr: 'Document', en: 'Document' },
  signature: { fr: 'Signature', en: 'Signature' },
  voice_note: { fr: 'Note vocale', en: 'Voice note' },
  prospect: { fr: 'Observation', en: 'Observation' },
  reading: { fr: 'Relevé', en: 'Reading' },
};

export function captureLabel(kind: string, declared: CaptureKind[], locale: Locale = 'fr'): string {
  if (isCoreCaptureKind(kind)) return CORE_LABELS[kind]?.[locale] ?? kind;
  const row = declared.find((candidate) => candidate.key === kind);
  return row ? text(row.name, locale) : kind;
}
